/**
 * Landing page after returning from ToyyibPay (billReturnUrl = /payment/success?ref=RK…)
 * Confirms the payment status with the server and refreshes the user's Access Pass.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, Card, CardContent, CircularProgress, Stack, Typography } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { IconTile } from '../ui';
import BrandLogo from '../layout/BrandLogo';
import { useLanguage } from '../../utils/LanguageContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { isAuthenticated } from '../../utils/api';

const text = {
  en: {
    checking: 'Confirming your payment…',
    successTitle: 'Payment successful',
    successDesc: 'Your Access Pass is now active. Thank you for supporting RumahKira!',
    pendingTitle: 'Payment is being processed',
    pendingDesc: 'This can take a minute. Your pass will activate automatically once the bank confirms.',
    failedTitle: 'Payment not completed',
    failedDesc: 'No money was taken, or the payment was cancelled. You can try again from your account.',
    signInTitle: 'Sign in to see your pass',
    signInDesc: 'Sign in with the account you used to pay.',
    account: 'Go to my account',
    estimate: 'Start an estimate',
    signIn: 'Sign in',
    retry: 'Check again',
  },
  my: {
    checking: 'Mengesahkan pembayaran anda…',
    successTitle: 'Pembayaran berjaya',
    successDesc: 'Pas Akses anda kini aktif. Terima kasih kerana menyokong RumahKira!',
    pendingTitle: 'Pembayaran sedang diproses',
    pendingDesc: 'Ini mungkin mengambil masa seminit. Pas anda akan aktif secara automatik selepas bank mengesahkan.',
    failedTitle: 'Pembayaran tidak selesai',
    failedDesc: 'Tiada wang diambil, atau pembayaran dibatalkan. Anda boleh cuba semula dari akaun anda.',
    signInTitle: 'Log masuk untuk melihat pas anda',
    signInDesc: 'Log masuk dengan akaun yang anda gunakan untuk membayar.',
    account: 'Ke akaun saya',
    estimate: 'Mula anggaran',
    signIn: 'Log masuk',
    retry: 'Semak semula',
  },
};

const MAX_CHECKS = 6;

const PaymentResultPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();
  const t = text[language] || text.en;
  const { checkPayment } = useSubscription();

  const ref = searchParams.get('ref') || searchParams.get('order_id');
  const billCode = searchParams.get('billcode');
  const gatewayStatus = searchParams.get('status_id'); // 1 success, 2 pending, 3 failed
  const [status, setStatus] = useState(isAuthenticated() ? 'checking' : 'signin');
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!isAuthenticated() || (!ref && !billCode)) {
      if (!isAuthenticated()) setStatus('signin');
      else setStatus(gatewayStatus === '1' ? 'pending' : 'failed');
      return undefined;
    }
    let cancelled = false;
    let timer;

    const run = async (n) => {
      try {
        const res = await checkPayment(ref, billCode);
        if (cancelled) return;
        const s = res?.data?.payment?.status;
        if (s === 'success' || s === 'completed') {
          setStatus('success');
        } else if (s === 'failed' || s === 'cancelled' || gatewayStatus === '3') {
          setStatus('failed');
        } else if (n + 1 < MAX_CHECKS) {
          setStatus('pending');
          timer = setTimeout(() => run(n + 1), 3000);
        } else {
          setStatus('pending');
        }
      } catch {
        if (!cancelled) setStatus(gatewayStatus === '3' ? 'failed' : 'pending');
      }
    };
    run(0);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [ref, billCode, gatewayStatus, checkPayment, attempt]);

  const views = {
    success: { icon: <CheckCircleRoundedIcon />, color: 'success', title: t.successTitle, desc: t.successDesc },
    pending: { icon: <HourglassTopRoundedIcon />, color: 'warning', title: t.pendingTitle, desc: t.pendingDesc },
    failed: { icon: <ErrorOutlineRoundedIcon />, color: 'error', title: t.failedTitle, desc: t.failedDesc },
    signin: { icon: <HourglassTopRoundedIcon />, color: 'primary', title: t.signInTitle, desc: t.signInDesc },
  };
  const view = views[status];

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, bgcolor: 'background.default' }}>
      <Box sx={{ width: '100%', maxWidth: 440 }}>
        <BrandLogo sx={{ justifyContent: 'center', mb: 3 }} />
        <Card>
          <CardContent sx={{ p: 4, textAlign: 'center' }}>
            {status === 'checking' ? (
              <Stack alignItems="center" spacing={2} sx={{ py: 3 }}>
                <CircularProgress />
                <Typography color="text.secondary">{t.checking}</Typography>
              </Stack>
            ) : (
              <>
                <IconTile icon={view.icon} color={view.color} size={64} sx={{ mx: 'auto', mb: 2 }} />
                <Typography variant="h5">{view.title}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 3 }}>
                  {view.desc}
                </Typography>
                <Stack spacing={1}>
                  {status === 'signin' ? (
                    <Button variant="contained" size="large" onClick={() => navigate('/auth')}>
                      {t.signIn}
                    </Button>
                  ) : (
                    <>
                      <Button variant="contained" size="large" onClick={() => navigate('/app/account')}>
                        {t.account}
                      </Button>
                      {status === 'pending' ? (
                        <Button onClick={() => { setStatus('checking'); setAttempt((a) => a + 1); }}>{t.retry}</Button>
                      ) : (
                        <Button onClick={() => navigate('/app')}>{t.estimate}</Button>
                      )}
                    </>
                  )}
                </Stack>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default PaymentResultPage;
